import { throttle } from 'lodash'
import { api } from 'api'
import { useMfaStore } from 'login/mfa/store/useMfaStore'
import * as SnackbarActions from '../global/snackbar/snackbar.actions'
import { t } from '@lingui/core/macro'
import { Bootstrap } from './types'
import { AppDispatch } from '@redux/store'
import { Account } from '@global/types'
import { trackEvent, EventTrackingEventType } from 'lib/eventTracking'

export enum AuthActionTypes {
  USER_LOGGED_IN = 'USER_LOGGED_IN',
  NOT_AUTHENTICATED = 'NOT_AUTHENTICATED',
  BOOTSTRAP_COMPLETE = 'BOOTSTRAP_COMPLETE',
  LOGOUT = 'LOGOUT',
  UPDATE_COMPANY_INFO = 'UPDATE_COMPANY_INFO',
}

interface UserLoggedInAction {
  type: AuthActionTypes.USER_LOGGED_IN
  payload: Bootstrap
}

interface NotAuthenticatedAction {
  type: AuthActionTypes.NOT_AUTHENTICATED
  payload: boolean
}

interface BootstrapCompleteAction {
  type: AuthActionTypes.BOOTSTRAP_COMPLETE
}

interface LogoutAction {
  type: AuthActionTypes.LOGOUT
}

interface UpdateCompanyInfoAction {
  type: AuthActionTypes.UPDATE_COMPANY_INFO
  payload: Account
}

export type AuthAction =
  | UserLoggedInAction
  | NotAuthenticatedAction
  | BootstrapCompleteAction
  | LogoutAction
  | UpdateCompanyInfoAction

const userLoggedInAction = (payload: Bootstrap): UserLoggedInAction => ({
  type: AuthActionTypes.USER_LOGGED_IN,
  payload,
})

const notAuthenticatedAction = (payload: boolean): NotAuthenticatedAction => ({
  type: AuthActionTypes.NOT_AUTHENTICATED,
  payload,
})

const BootstrapCompleteAction = (): BootstrapCompleteAction => ({
  type: AuthActionTypes.BOOTSTRAP_COMPLETE,
})

const logoutAction = (): LogoutAction => ({
  type: AuthActionTypes.LOGOUT,
})

const updateCompanyInfoAction = (payload: Account): UpdateCompanyInfoAction => ({
  type: AuthActionTypes.UPDATE_COMPANY_INFO,
  payload,
})

export function getUser(sessionToe: string) {
  return async (dispatch: AppDispatch) => {
    localStorage.setItem('session_toe', sessionToe)

    try {
      const { data } = await api.get<Bootstrap>('/api/v1/bootstrap', { withCredentials: true })
      dispatch(userLoggedInAction(data))
      startInactivityTimer(dispatch, data)
    } catch {
      dispatch(notAuthenticatedAction(false))
    } finally {
      dispatch(BootstrapCompleteAction())
    }
  }
}

export function logout() {
  return (dispatch: AppDispatch) =>
    api.post('/api/v1/logout', null, { withCredentials: true }).finally(() => {
      localStorage.removeItem('session_toe')
      useMfaStore.getState().resetMfa()
      clearInactivityTimerTimestamp()
      dispatch(logoutAction())
    })
}

export function updateCompanyInfo(company: Account) {
  return (dispatch: AppDispatch) => {
    dispatch(updateCompanyInfoAction(company))
  }
}

export const forgotPassword = (email: string) => api.post('/forgot-password', { email })

interface ResetPassword {
  password: string
  password_confirmation: string
  token: string
}
export const resetPassword = (reset: ResetPassword) => api.post('/reset-password', reset)

export const getSignup = (token: string) => api.get(`/signup/user/${token}`)

interface ConfirmSignup {
  password: string
  password_confirmation: string
  token: string
  email: string
  name: string
}
export const confirmSignup = (payload: ConfirmSignup) =>
  api.post(`/signup/complete/${payload.token}`, payload)

const INACTIVITY_LOCAL_STORAGE_KEY = 'lastActionTime'
const getInactivityTimerTimestamp = () => {
  const lastActiveTimeString = localStorage.getItem(INACTIVITY_LOCAL_STORAGE_KEY)
  return lastActiveTimeString && lastActiveTimeString !== 'null'
    ? parseInt(lastActiveTimeString, 10)
    : Date.now()
}
export const updateInactivityTimerTimestamp = throttle(() => {
  localStorage.setItem(INACTIVITY_LOCAL_STORAGE_KEY, Date.now().toString())
}, 1000)
export const clearInactivityTimerTimestamp = () => {
  localStorage.removeItem(INACTIVITY_LOCAL_STORAGE_KEY)
}

function startInactivityTimer(dispatch: AppDispatch, boostrapData: Bootstrap) {
  updateInactivityTimerTimestamp()
  const timer = new Blob([
    // eslint-disable-next-line lingui/no-unlocalized-strings
    `
      let interval = null;
      onmessage = function(e) {
          if(e.data === 'start'){
              interval = setInterval(function(){
                  postMessage('LOGOUT REQUIRED');
              }, 1000);
          } else {
              clearInterval(interval);
          }
      }
  `,
  ])

  function mouseMoved() {
    updateInactivityTimerTimestamp()
  }

  function checkLogout() {
    const oneHourInMs = 1000 * 60 * 60 // 1000ms * 60s * 60m = 1 hr in ms
    const lastActiveTime = getInactivityTimerTimestamp()
    if (lastActiveTime + oneHourInMs < Date.now()) {
      void fireLogout(boostrapData)
    }
  }

  // probably debounce this. But that's for cowards.
  document.addEventListener('mousemove', mouseMoved)

  // start a webworker for accurate timing power.
  const timerURL = window.URL.createObjectURL(timer)
  const worker = new Worker(timerURL)

  worker.onmessage = function () {
    checkLogout()
  }
  worker.postMessage('start') // Start the worker.
  document.addEventListener('visibilitychange', checkLogout)

  async function fireLogout(boostrapData: Bootstrap) {
    const data = {
      lastActiveTime: getInactivityTimerTimestamp(),
      now: Date.now(),
      user: boostrapData.user.id,
      account: boostrapData.currentAccount.id,
    }
    trackEvent(EventTrackingEventType.INACTIVITY_TIMEOUT, data)

    // clean up worker
    worker.postMessage('stop')
    worker.terminate()

    // clean up global event listeners
    document.removeEventListener('mousemove', mouseMoved)
    document.removeEventListener('visibilitychange', checkLogout)

    // use the same logout action so that logic is shared
    await dispatch(logout())

    // broadcast a message visible to the user
    dispatch(
      SnackbarActions.showSnackbar({
        message: t`Due to inactivity you have been logged out.`,
        type: 'INFO',
      }),
    )
  }
}
